import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { ColumnVisibilityDelta } from "../domain/registerConfig";

/**
 * Device-local UI state. EXACTLY these keys — theme, tenantKey, platformOverride, mySiteId,
 * columnLayouts, hiddenModules. Nothing server-side, no profile, and no identity: who is signed
 * in is MSAL's, behind `src/api/session.ts`, in `sessionStorage` (AU-D13).
 *
 * `tenantKey` is the session seam's SOURCE, not its API: only `src/api/session.ts` touches it.
 * Everything else reads `useSession()`.
 *
 * `tenantKey` is device-local deliberately (F-D3). It is a *selection*, not a credential: the
 * API authorises every request against the principal's membership in whatever tenant the
 * `X-LL-Tenant` header names, so a stale or hand-edited value in localStorage buys nothing.
 * Keeping it here means the same person on two machines can be working in two tenants, which is
 * the behaviour someone who administers two of them actually wants.
 */
export type ThemePreference = "light" | "dark" | "system";
export type PlatformOverride = "auto" | "desktop" | "tablet" | "phone";

export interface UiState {
  theme: ThemePreference;
  /** The selected tenant's key, sent as `X-LL-Tenant`. Null until one is chosen. */
  tenantKey: string | null;
  platformOverride: PlatformOverride;
  mySiteId: string | null;
  /** Per register key: the device-local show/hide delta over the width baseline. */
  columnLayouts: Record<string, ColumnVisibilityDelta>;
  /** Dev only: module keys hidden from the shell. */
  hiddenModules: string[];
  setTheme: (theme: ThemePreference) => void;
  setTenantKey: (tenantKey: string | null) => void;
  setPlatformOverride: (platformOverride: PlatformOverride) => void;
  setMySiteId: (mySiteId: string | null) => void;
  setColumnLayout: (key: string, layout: ColumnVisibilityDelta) => void;
  setHiddenModules: (hiddenModules: string[]) => void;
}

const memoryStorage = (): Storage => {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear: () => map.clear(),
    getItem: (key: string) => map.get(key) ?? null,
    key: (index: number) => Array.from(map.keys())[index] ?? null,
    removeItem: (key: string) => void map.delete(key),
    setItem: (key: string, value: string) => void map.set(key, value),
  } as Storage;
};

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: "system",
      // No tenant until sign-in picks one from `GET /me/tenants`.
      tenantKey: null,
      platformOverride: "auto",
      mySiteId: null,
      columnLayouts: {},
      hiddenModules: [],
      setTheme: (theme) => set({ theme }),
      setTenantKey: (tenantKey) => set({ tenantKey }),
      setPlatformOverride: (platformOverride) => set({ platformOverride }),
      setMySiteId: (mySiteId) => set({ mySiteId }),
      setColumnLayout: (key, layout) =>
        set((state) => ({ columnLayouts: { ...state.columnLayouts, [key]: layout } })),
      setHiddenModules: (hiddenModules) => set({ hiddenModules }),
    }),
    {
      name: "levelload.ui",
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? memoryStorage() : window.localStorage,
      ),
    },
  ),
);

/** Resolves `system` against the device and returns the concrete theme. */
export function resolveTheme(preference: ThemePreference): "light" | "dark" {
  if (preference !== "system") return preference;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Flips the `dark` class on the document root. Safe to call repeatedly. */
export function applyTheme(preference: ThemePreference): void {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(preference);
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document.documentElement.style.colorScheme = resolved;
}
